import type { IChassisIdentifier, IDriveLine, IUnit } from '@/service/vpc/types/IUnit';

import ChassisPositionNames from '@/assets/data/chassis_position_names.json';

export const tryFindUnit = (chassisId: string, units: IUnit[]): IUnit | undefined => {
  return units.find((unit) =>
    unit.installations
      .flatMap((i) => i.driveLines)
      .some(
        (d) =>
          getId({
            chassisSeries: d.chassisSeries,
            chassisNumber: d.chassisNumber,
          }) === chassisId.toUpperCase()
      )
  );
};

export const getId = (chassis: IChassisIdentifier): string => {
  const paddedChassisNumber = (chassis.chassisNumber ?? '0').toString().padStart(6, '0');
  return `${chassis.chassisSeries}${paddedChassisNumber}`.replaceAll(' ', '');
};

export const parseChassisIdFromDriveline = (driveline: IDriveLine): string => {
  const paddedChassisNumber = (driveline.chassisNumber ?? '0').toString().padStart(6, '0');
  return `${driveline.chassisSeries}${paddedChassisNumber}`.replaceAll(' ', '');
};

export const getDrivelinePosition = (unit: IUnit, chassisId: string) => {
  const installations = unit.installations.find((inst) =>
    inst.driveLines.some(
      (dl) => getId({ chassisSeries: dl.chassisSeries, chassisNumber: dl.chassisNumber }) === chassisId
    )
  );
  const driveline = unit?.installations
    .flatMap((i) => i.driveLines)
    .find((d) => getId({ chassisSeries: d.chassisSeries, chassisNumber: d.chassisNumber }) === chassisId);

  const drivelinePositions = ChassisPositionNames.chassisPositions.find(
    (cp) => cp.mainSegment === unit.mainSegment && cp.installationTypes.includes(installations?.installationType || '')
  )?.drivelinePositions;

  const positions = drivelinePositions?.find(
    (dp) => dp.drivelineCount === installations?.numberOfDrivelines
  )?.positions;

  const positionName =
    installations?.numberOfDrivelines === 1
      ? positions?.[0]
      : positions?.find((p) => p.position === driveline?.position);

  return {
    position: driveline?.position || 0,
    positionName: positionName ? positionName.chassisPositionDisplayName : '',
  };
};

export const extractChassisIdsCsv = (units: IUnit[]): string => {
  return units
    .map((u) => getUnitsChassisIds(u))
    .flat()
    .join(',');
};

export const extractChassisIds = (units: IUnit[]): string[] => {
  return units
    .flatMap((u) => u.installations)
    .flatMap((i) => i.driveLines)
    .map(parseChassisIdFromDriveline);
};

export const getUnitSerialNumbers = (unit: IUnit | null | undefined): string[] => {
  if (!unit) return [];
  return unit.installations
    .flatMap((i) => i.driveLines)
    .flatMap((d) => d.individualProducts)
    .map((ip) => String(ip.serialNumber))
    .filter((value, index, self) => self.indexOf(value) === index); // Unique serial numbers
};

export const getUnitsChassisIds = (unit: IUnit | null | undefined): string[] => {
  if (!unit) return [];

  return unit.installations
    .flatMap((i) => i.driveLines)
    .map((d) =>
      getId({
        chassisSeries: d.chassisSeries,
        chassisNumber: d.chassisNumber,
      })
    );
};

export const getUnitsNonPaddedChassisIds = (unit: IUnit | null | undefined): string[] => {
  if (!unit) return [];

  return unit.installations
    .flatMap((i) => i.driveLines)
    .map((d) => `${d.chassisSeries}${d.chassisNumber}`.replaceAll(' ', ''));
};

export const getUnitsChassisIdentifier = (unit: IUnit | undefined): IChassisIdentifier[] => {
  if (!unit) return [];
  return unit.installations
    .flatMap((i) => i.driveLines)
    .map((d) => ({
      chassisSeries: d.chassisSeries,
      chassisNumber: d.chassisNumber,
    }));
};

export const getDriveLine = (unit: IUnit | undefined, chassisId: string): IDriveLine | undefined => {
  return unit?.installations
    .flatMap((i) => i.driveLines)
    .find((d) => getId({ chassisSeries: d.chassisSeries, chassisNumber: d.chassisNumber }) === chassisId);
};

export const getUnitChassiControlSystems = (unit: IUnit | null | undefined): string[] => {
  if (!unit) return [];
  return unit.installations
    .filter((i) => i.installationType === 'ControlSystem')
    .flatMap((i) => i.driveLines)
    .map((d) =>
      getId({
        chassisNumber: d.chassisNumber,
        chassisSeries: d.chassisSeries,
      })
    );
};

export const parseChassisId = (chassisId: string): IChassisIdentifier | null => {
  const chassisIdRegex = /^([A-Z]+)(\d+)$/;
  const match = chassisId.match(chassisIdRegex);
  if (match) {
    return {
      chassisSeries: match[1],
      chassisNumber: parseInt(match[2], 10),
    };
  }
  return null;
};

// A VPDRV driveline marks an IPS40 marine unit; these get the dashboard regardless of
// telemetry device or service status (AB#1611582).
export const isIps40 = (unit: IUnit): boolean =>
  unit.installations.some((i) => i.driveLines.some((d) => d.chassisSeries?.trim() === 'VPDRV'));
